/**
 * Date tools.
 */
H_Tools.date = (() => {
    // console.log('Date tools initialized');

    /**
     * Checks if the supplied items represent valid dates and are in order. Returns a representative
     * boolean value.
     *
     * @param {...any} items One or more strings or date objects (as produced by the toDateObject
     *                       -function). A string needs to contain a date in `dd.mm.yyyy` format
     * @returns              `true` if all supplied strings and objects represents a valid date and
     *                       are in order, `false` otherwise
     */
    function areInOrder(...items) {
        let previousItem;
        for (let currentItem of items) {
            if (typeof currentItem === 'string') {
                currentItem = toDateObject(currentItem);
            }

            if (!isDate(currentItem)) {
                return false;
            }

            if (!previousItem) {
                previousItem = currentItem;
                continue;
            }

            if (
                new Date(
                    currentItem.year,
                    currentItem.month - 1,
                    currentItem.day
                ).getTime() <
                new Date(
                    previousItem.year,
                    previousItem.month - 1,
                    previousItem.day
                ).getTime()
            ) {
                return false;
            }

            previousItem = currentItem;
        }

        return true;
    }

    /**
     * Checks if the supplied items represent valid dates. Returns a representative boolean value.
     *
     * @param {...any} items One or more strings or date objects (as produced by the toDateObject
     *                       -function). A string needs to contain a date in `dd.mm.yyyy` format
     * @returns              `true` if all supplied strings and objects represents a date, `false`
     *                       otherwise
     */
    function isDate(...items) {
        for (let object of items) {
            if (typeof object === 'string') {
                object = toDateObject(object);
            }

            if (typeof object !== 'object') {
                return false;
            }

            if (
                !Number.isInteger(object.day) ||
                !Number.isInteger(object.month) ||
                !Number.isInteger(object.year)
            ) {
                return false;
            }

            if (!isValid(object)) {
                return false;
            }
        }
        return true;
    }

    /**
     * Checks if the supplied date object is a valid date. Returns a representative boolean value.
     *
     * Here "valid" means that:
     * - The year is between 1 and 9999
     * - Month is between 1 and 12
     * - Day is between 1 and the appropriate maximum day number (specific to each month)
     *
     * @param {*} date A date object (as produced by the toDateObject -function)
     */
    function isValid(date) {
        if (date.year < 1 || date.month < 1 || date.day < 1) {
            return false;
        }

        if (date.year > 9999 || date.month > 12 || date.day > 31) {
            return false;
        }

        // Leap year February
        if (date.year % 4 === 0 && date.month === 2 && date.day > 29) {
            return false;
        }

        // Non-leap year February
        if (date.month === 2 && date.day > 28) {
            return false;
        }

        // Months with 30 days
        if ([4, 6, 9, 11].includes(date.month) && date.day > 30) {
            return false;
        }

        return true;
    }

    /**
     * Converts the supplied string into a date object, which will contain the following properties:
     * - day
     * - month
     * - year
     *
     * @param {*} string A string that needs to contain a date in `dd.mm.yyyy` format
     */
    function toDateObject(string) {
        if (typeof string !== 'string') {
            return {
                day: 0,
                month: 0,
                year: 'nan', // return a string here instead of 0 to make isDate checks to fail
            };
        }

        const parts = string.split('.');
        return parts.length === 3
            ? {
                  day: parseInt(parts[0]),
                  month: parseInt(parts[1]),
                  year: parseInt(parts[2]),
              }
            : {
                  day: 0,
                  month: 0,
                  year: 'nan', // return a string here instead of 0 to make isDate checks to fail
              };
    }

    // return an object to expose inner functions
    return {
        areInOrder: areInOrder,
        isDate: isDate,
        toDateObject: toDateObject,
    };
})();
